/**
 * Central path mapper for customer APIs.
 *
 * solutions-dashboard-service exposes customer endpoints under `/v1/customer`
 * with plural collections (`/organisations`, `/apps`, `/users`, `/zpins`, …).
 *
 * Prefix resolution:
 * - `REACT_APP_CUSTOMER_API_PREFIX` when set (e.g. `/v1/customer`)
 * - empty when `REACT_APP_SERVER_URI` already ends with `/api/customer` or `/v1/customer`
 * - `/v1/customer` for direct connections (e.g. localhost:8080)
 */

function serverBase(): string {
  return (process.env.REACT_APP_SERVER_URI || "").replace(/\/$/, "");
}

function customerApiPrefix(): string {
  if (process.env.REACT_APP_CUSTOMER_API_PREFIX !== undefined) {
    return process.env.REACT_APP_CUSTOMER_API_PREFIX;
  }

  const base = serverBase();
  if (/\/(api|v1)\/customer$/i.test(base)) {
    return "";
  }

  return "/v1/customer";
}

/** Public SDS routes (no customer JWT required). */
function publicApiPrefix(): string {
  const customerPrefix = customerApiPrefix();
  if (customerPrefix === "") {
    const base = serverBase();
    if (/\/v1\/customer$/i.test(base)) {
      return base.replace(/\/v1\/customer$/i, "/v1/public");
    }
    if (/\/api\/customer$/i.test(base)) {
      return base.replace(/\/api\/customer$/i, "/v1/public");
    }
  }
  return customerPrefix.replace(/\/v1\/customer$/i, "/v1/public") || "/v1/public";
}

function matchesPath(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

const CUSTOMER_PREFIXED_ROOTS = [
  "/organisation",
  "/organisations",
  "/user",
  "/products",
  "/plans",
  "/apps",
  "/keys",
  "/zpins",
  "/batch",
  "/productBatchService",
  "/estamp",
];

export function mapCustomerPath(rawUrl: string): string {
  if (!rawUrl || !rawUrl.startsWith("/")) return rawUrl;
  if (rawUrl.startsWith("/v1/")) return rawUrl;

  const prefix = customerApiPrefix();
  const qIndex = rawUrl.indexOf("?");
  const path = qIndex === -1 ? rawUrl : rawUrl.slice(0, qIndex);
  const query = qIndex === -1 ? "" : rawUrl.slice(qIndex);

  if (matchesPath(path, "/otp")) return rawUrl;

  const to = (p: string) => `${prefix}${p}${query}`;
  const toPublic = (p: string) => `${publicApiPrefix()}${p}${query}`;

  // Public catalog data — canonical on /v1/public (legacy /countries and signup categories).
  if (path === "/countries") {
    return toPublic("/countries");
  }
  if (path === "/organisations/categories") {
    return toPublic("/organisations/categories");
  }

  // Legacy singular catalog paths → SDS plural collections.
  if (path === "/product") {
    return to("/products");
  }
  if (path === "/plan") {
    return to("/plans");
  }

  // Legacy org products alias → unified catalog list (org resolved from session).
  if (path === "/products/org") {
    return to("/products");
  }

  // Former REST list: GET /organisations/:id/products → GET /products.
  // Keep subscribe/unsubscribe on the org-scoped mutation paths.
  const orgProductsList = path.match(/^\/organisations\/([^/]+)\/products$/);
  if (orgProductsList) {
    const orgId = orgProductsList[1];
    if (query.includes("organisation_pid=")) {
      return to("/products");
    }
    const mergedQuery = query
      ? `${query}&organisation_pid=${encodeURIComponent(orgId)}`
      : `?organisation_pid=${encodeURIComponent(orgId)}`;
    return `${prefix}/products${mergedQuery}`;
  }

  if (
    CUSTOMER_PREFIXED_ROOTS.some((root) => matchesPath(path, root)) ||
    path === "/payment/create" ||
    path === "/doc" ||
    path === "/demoPortal/product"
  ) {
    return to(path);
  }

  return rawUrl;
}
